
import React from "react";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "../ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ModernPageHeader from "../layout/ModernPageHeader";

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  tabs?: Array<{
    id: string;
    label: string;
    icon?: React.ReactNode;
    badge?: number;
  }>;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

const AdminLayout = ({ 
  children, 
  title, 
  description,
  actions,
  tabs,
  activeTab,
  onTabChange
}: AdminLayoutProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function checkAdminStatus() {
      try {
        // Get the current user ID
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user?.id) {
          throw new Error("User not authenticated");
        }
        
        // Get the user's profile to find organization
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        
        if (profileError) throw profileError;
        if (!profileData?.organization_id) {
          throw new Error("User has no organization");
        }
        
        // Check if the organization is a super admin
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('is_super_admin')
          .eq('id', profileData.organization_id)
          .single();
        
        if (orgError) throw orgError;
        
        // Set admin status based on organization flag
        const isAdminOrg = orgData?.is_super_admin || false;
        setIsAdmin(isAdminOrg);
        
        if (!isAdminOrg) {
          toast({
            title: "Access denied",
            description: "You don't have permission to access this page",
            variant: "destructive",
          });
          navigate("/");
        }
      } catch (error: any) {
        console.error("Error checking admin status:", error);
        toast({
          title: "Error",
          description: "Failed to verify admin privileges",
          variant: "destructive",
        });
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    }
    
    checkAdminStatus();
  }, [navigate, toast]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect from the useEffect
  }

  // Add the Shield icon to the title
  const headerTitle = (
    <div className="flex items-center">
      <Shield className="h-5 w-5 text-primary mr-2" />
      {title}
    </div>
  );

  return (
    <div className="container mx-auto max-w-4xl bg-white h-full flex flex-col">
      <ModernPageHeader
        title={title}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        actions={actions}
      />
      
      {description && (
        <div className="px-8 pt-2 pb-4">
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      )}
      
      <div className="px-8 pb-8 flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
